import { TYPE_LABELS } from "@/lib/complaintTypes";
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import KPICard from "@/components/KPICard";
import { api } from "@/lib/api";
import { BarChart3, Loader2, CheckCircle2, TrendingUp, Percent, Clock } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#1d4ed8", "#2563eb", "#0ea5e9", "#8b5cf6", "#06b6d4", "#10b981", "#f97316"];

export default function Statistics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/stats/iso");
      setData(data);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <Header title="Statistiques" subtitle="Indicateurs ISO 9001 · Performance qualité" />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-6">
        {loading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard label="Total litiges" value={data.total_complaints} icon={BarChart3} accent="blue" />
              <KPICard label="Délai moyen résolution" value={`${data.avg_resolution} j`} icon={Clock} accent="amber" />
              <KPICard
                label="Efficacité actions"
                value={`${data.efficacy}%`}
                icon={CheckCircle2}
                accent="emerald"
                hint={`${data.ineffective_count} inefficace(s)`}
              />
              <KPICard
                label="Taux de récurrence"
                value={`${data.recurrence_rate}%`}
                icon={Percent}
                accent="rose"
                hint="Clients avec ≥2 litiges"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel title="Litiges par type">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.by_type.map((d) => ({ ...d, name: TYPE_LABELS[d.name] || d.name }))}
                      dataKey="value" nameKey="name" outerRadius={90}
                    >
                      {data.by_type.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Panel>

              <Panel title="Top clients en volume de litiges">
                {data.top_clients.length === 0 ? (
                  <div className="text-slate-400 text-sm py-8 text-center">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.top_clients} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={150} />
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#1d4ed8" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>

              <Panel title="Top fournisseurs en litiges">
                {data.top_suppliers.length === 0 ? (
                  <div className="text-slate-400 text-sm py-8 text-center">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.top_suppliers} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={150} />
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#0ea5e9" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>

              <Panel title="Top transporteurs en litiges">
                {(data.top_carriers || []).length === 0 ? (
                  <div className="text-slate-400 text-sm py-8 text-center">Aucune donnée</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.top_carriers} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={150} />
                      <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            </div>
          </>
        )}
      </main>
    </>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6">
      <h3 className="text-lg font-semibold text-slate-900 font-heading mb-4 flex items-center gap-2">
        <TrendingUp size={18} strokeWidth={1.5} /> {title}
      </h3>
      {children}
    </div>
  );
}
